import { ProductType } from '../../protocol/gen/warinspace/v2/common_pb.js';

export const SHOP_TABS = ['decal', 'trail', 'tag', 'skin', 'credits'] as const;

export type ShopTab = (typeof SHOP_TABS)[number];

export const SHOP_TAB_LABELS: Readonly<Record<ShopTab, string>> = {
  decal: 'Decal',
  trail: 'Trail',
  tag: 'Tag',
  skin: 'Skin',
  credits: 'Coming Soon',
};

/** `ProductType.TAG_OR_TRAIL` covers both tabs on the wire; a trail added to the catalogue but not to this set appears under Tag and does not draw. */
export const TRAIL_PRODUCT_IDS: ReadonlySet<number> = new Set([
  8, 9, 10, 50, 51, 52, 53, 54, 105, 125,
]);

export type CosmeticSlot = 'decal' | 'trail' | 'tag' | 'skin';

export function shipsTabLabel(shipCustomizationEnabled: boolean): string {
  return shipCustomizationEnabled ? 'Ships' : SHOP_TAB_LABELS.skin;
}

export interface Product {
  readonly id: number;
  readonly name: string;
  readonly price: number;
  readonly tab: ShopTab;
  readonly slot: CosmeticSlot | null;
  readonly imagePath: string;
  readonly shipId: number;
  readonly hued: boolean;
  readonly purchasable: boolean;
}

/** `customData` is `URL[:extra]`; split on the first colon, since the URL itself is a path and may contain none of its own. */
export function parseCustomData(customData: string): { url: string; extra: string } {
  const colon = customData.indexOf(':');
  return colon < 0
    ? { url: customData, extra: '' }
    : { url: customData.slice(0, colon), extra: customData.slice(colon + 1) };
}

export function productFrom(row: {
  productId: number;
  name: string;
  price: number;
  productType: ProductType;
  customData: string;
}): Product {
  const { url, extra } = parseCustomData(row.customData);
  const tab = tabFor(row.productType, row.productId);
  return {
    id: row.productId,
    name: row.name,
    price: row.price,
    tab,
    slot: tab === 'credits' ? null : tab,
    imagePath: url,
    shipId: tab === 'skin' ? Number.parseInt(extra.split(':')[0] ?? '', 10) || 0 : 0,
    hued: tab === 'decal' || (tab === 'skin' && Number(extra.split(':')[1] ?? '') > 0),
    purchasable: row.price >= 0,
  };
}

function tabFor(type: ProductType, productId: number): ShopTab {
  switch (type) {
    case ProductType.DECAL:
      return 'decal';
    case ProductType.TAG_OR_TRAIL:
      return TRAIL_PRODUCT_IDS.has(productId) ? 'trail' : 'tag';
    case ProductType.SKIN:
      return 'skin';
    case ProductType.PAYMENT_TILE:
      return 'credits';
    default:
      return 'tag';
  }
}
